/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Fiche } from '@mairistem/fiche';

import { Segment } from '@mairistem/composants';

import { MentionListeContainer } from '@/components/Mention/Liste';

import type { FicheConfig } from './ficheConfig';
import {
  InformationsRegistreActe,
  InformationsEvenement,
  InformationsIndividu,
  TabsPersonnes,
} from './Blocs';
import { CLES_INDIVIDU_DOSSIER } from './Blocs';
import type { PersonneTab } from './Blocs';

/**
 * Individus d'un dossier, indexés par clé. Tous optionnels : le jeu dépend du
 * type d'acte.
 */
type Individus = Partial<Record<(typeof CLES_INDIVIDU_DOSSIER)[number], any>>;

/**
 * Extrait du dossier renvoyé par l'API les seules clés d'individu connues.
 *
 * ⚠️ Sur `CLES_INDIVIDU_DOSSIER` et non sur les clés d'onglet : un onglet de
 * parents de mariage porte DEUX individus et n'est donc pas lui-même une clé du
 * dossier. Pour les trois autres types d'acte, les deux listes coïncident.
 */
const extraireIndividus = (dossier?: Record<string, any>): Individus =>
  _.pick(dossier ?? {}, CLES_INDIVIDU_DOSSIER);

interface ActeFicheProps {
  loading?: boolean;
  /** Dossier complet renvoyé par l'API : `{ acte, enfant, parent1, parent2, mentions }` */
  acte?: any;
  /**
   * Configuration du type d'acte (URL, titres, descripteurs de blocs, modales).
   * C'est elle qui porte tout ce qui variait auparavant en dur dans ce fichier.
   */
  config: FicheConfig;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  /** Ouvre automatiquement la modale « Ajouter une mention » au montage. */
  autoOpenAjoutMention?: boolean;
}

const ActeFiche = (props: ActeFicheProps) => {
  const {
    loading,
    acte: acteProps,
    config,
    service,
    autoOpenAjoutMention,
  } = props;

  const [acte, setActe] = React.useState<any>(acteProps?.acte);

  /**
   * Les individus du dossier, indexés par leur onglet.
   *
   * Un seul état plutôt qu'un par rôle : le jeu d'individus varie selon le type
   * d'acte (enfant / parents pour une naissance, défunt / parents / déclarant
   * pour un décès, époux et témoins à venir pour un mariage). Les indexer par
   * `PersonneTab` permet au composant de rester le même quel que soit ce jeu.
   */
  const [individus, setIndividus] = React.useState<Individus>(() =>
    extraireIndividus(acteProps),
  );

  /**
   * Premier onglet du type d'acte, donc individu principal : l'enfant d'une
   * naissance, le défunt d'un décès, le 1er conjoint d'un mariage. Sert de
   * source à ce qui s'y rapporte sans dépendre du rôle exact.
   *
   * ⚠️ Le premier onglet désigne toujours UN individu — un onglet qui en porte
   * plusieurs (les parents d'un conjoint de mariage) n'ouvre jamais la fiche.
   * D'où le transtypage : le type d'onglet, lui, est plus large.
   */
  const cleIndividuPrincipal = config.onglets[0]?.key;
  /*
   * ⚠️ Le premier onglet désigne toujours UN individu — un onglet qui en porte
   * plusieurs (les parents d'un conjoint de mariage) n'est jamais le premier.
   * C'est ce qui autorise ce transtypage, le type d'onglet étant plus large.
   */
  const cleIndividuPrincipalDossier = cleIndividuPrincipal as keyof Individus;

  const [activePersonneTab, setActivePersonneTab] =
    React.useState<PersonneTab>(cleIndividuPrincipal);

  React.useEffect(() => {
    if (!_.isNil(acteProps)) {
      setActe(acteProps?.acte);
      setIndividus(extraireIndividus(acteProps));
    }
  }, [acteProps]);

  const handleActeSaved = React.useCallback((updatedActe: any) => {
    setActe(updatedActe);
  }, []);

  /**
   * Remplace un individu du dossier après son enregistrement.
   *
   * ⚠️ Indexé par clé d'INDIVIDU et non par onglet : un onglet de parents de
   * mariage en porte deux, et c'est bien l'individu qu'on remplace.
   */
  const handleIndividuSaved = React.useCallback(
    (cle: keyof Individus) => (individu: any) =>
      setIndividus((prev) => ({ ...prev, [cle]: individu })),
    [],
  );

  /** Dossier recomposé depuis l'état local, source des descripteurs. */
  const dossier = React.useMemo(
    () => ({ acte, ...individus }),
    [acte, individus],
  );

  const informationsRegistrePanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="informationsRegistreActe"
        id="informationsRegistreActe"
        title="REGISTRE ET ACTE"
        open
      >
        <InformationsRegistreActe
          acte={acte}
          sections={config.blocs.registreActe(dossier)}
          Modal={config.modales?.registreActe}
          propsModal={config.propsModaleRegistre}
          service={service}
          onSaved={handleActeSaved}
        />
      </Fiche.Panel>
    ),
    [acte, dossier, config, service, handleActeSaved],
  );

  const informationsEvenementPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="informationsEvenement"
        id="informationsEvenement"
        title={config.titrePanelEvenement}
        open
      >
        <InformationsEvenement
          identifiant={acte?.identifiant}
          // Le bloc du décès affiche le lieu, porté par l'individu principal :
          // sa modale doit pouvoir l'écrire, d'où ces trois props.
          individuPrincipal={individus[cleIndividuPrincipalDossier]}
          urlIndividus={config.urlIndividus}
          onIndividuSaved={handleIndividuSaved(cleIndividuPrincipalDossier)}
          // Naissance : l'évènement se décrit depuis l'enfant. Sur un type dont
          // l'évènement est porté par l'acte (reconnaissance, décès), le
          // descripteur lit `acte` et cette prop n'est pas consultée.
          enfant={individus.enfant}
          acte={acte}
          sections={config.blocs.evenement(dossier)}
          Modal={config.modales?.evenement}
          service={service}
          onSaved={
            config.evenementPorteParLActe
              ? handleActeSaved
              : handleIndividuSaved(cleIndividuPrincipalDossier)
          }
        />
      </Fiche.Panel>
    ),
    [
      acte,
      individus,
      cleIndividuPrincipalDossier,
      dossier,
      config,
      service,
      handleIndividuSaved,
      // L'évènement d'une reconnaissance ou d'un décès patche l'ACTE.
      handleActeSaved,
    ],
  );

  const informationsPersonnesPanel = React.useMemo(() => {
    /*
     * Individu de l'onglet actif — `undefined` sur un onglet qui en porte
     * plusieurs, où chaque section déclare le sien (`cleIndividu`).
     */
    const individu = individus[activePersonneTab as keyof Individus];

    return (
      // L'arrondi de 14px des panels vient de `.ui.styled.accordion .accordion.fiche-panel` :
      // ce Segment n'a pas la classe `accordion`, il faut donc le poser à la main.
      <Segment
        id="informationsPersonnes"
        className="fiche-panel-individu"
        title="INDIVIDUS"
      >
        <TabsPersonnes
          active={activePersonneTab}
          onChange={setActivePersonneTab}
          tabs={config.onglets}
        />
        <Segment basic>
          <InformationsIndividu
            individu={individu}
            // Un onglet de parents de mariage décrit deux individus : chaque
            // section dit lequel, le panneau les résout ici.
            individus={individus}
            tab={activePersonneTab}
            sections={config.blocs.individu(
              individu,
              activePersonneTab,
              dossier,
            )}
            identifiant={acte?.identifiant}
            // Date de naissance de l'individu principal, sur laquelle porte le
            // contrôle « parent né avant lui » : l'enfant d'une naissance, le
            // défunt d'un décès.
            naissanceDateIndividuPrincipal={
              individus[cleIndividuPrincipalDossier]?.naissanceDate
            }
            urlIndividus={config.urlIndividus}
            ModalIndividu={config.modales?.individus?.[activePersonneTab]}
            ModalParent={config.modales?.parent}
            propsModalParent={config.propsModaleParent}
            // La section « ETAT MATRIMONIAL » du défunt est portée par l'ACTE :
            // sa modale l'édite, donc elle le reçoit et sait le rafraîchir.
            acte={acte}
            service={service}
            /*
             * La clé vient du GROUPE quand il en déclare une : sur un onglet à
             * plusieurs individus, l'onglet ne dit pas lequel a été enregistré.
             */
            onSaved={(individuEnregistre: any, cleIndividu?: string) =>
              handleIndividuSaved(
                (cleIndividu ?? activePersonneTab) as keyof Individus,
              )(individuEnregistre)
            }
            onActeSaved={handleActeSaved}
          />
        </Segment>
      </Segment>
    );
  }, [
    activePersonneTab,
    acte,
    individus,
    cleIndividuPrincipalDossier,
    dossier,
    config,
    service,
    handleIndividuSaved,
    // La modale du défunt écrit aussi sur l'acte (état matrimonial).
    handleActeSaved,
  ]);

  /**
   * QUATRIÈME panneau, propre au type d'acte : absent tant que le registre ne le
   * décrit pas.
   *
   * Le mariage y range son contrat, que la maquette sort de la célébration —
   * crayon propre, et le notaire dedans plutôt que dans un onglet.
   *
   * Rendu par `InformationsEvenement`, dont la forme est exactement celle d'un
   * panneau à sections plates avec une modale : un composant de plus n'aurait
   * rien apporté.
   */
  const complementPanel = React.useMemo(() => {
    if (!config.blocs.complement || !config.titrePanelComplement) {
      return null;
    }

    const cleComplement = config.cleIndividuComplement as keyof Individus;

    return (
      <Fiche.Panel
        key="informationsComplement"
        id="informationsComplement"
        title={config.titrePanelComplement}
        open
      >
        <InformationsEvenement
          identifiant={acte?.identifiant}
          individuPrincipal={
            cleComplement ? individus[cleComplement] : undefined
          }
          urlIndividus={config.urlIndividus}
          onIndividuSaved={
            cleComplement ? handleIndividuSaved(cleComplement) : undefined
          }
          acte={acte}
          sections={config.blocs.complement(dossier)}
          Modal={config.modales?.complement}
          service={service}
          onSaved={handleActeSaved}
        />
      </Fiche.Panel>
    );
  }, [
    acte,
    individus,
    dossier,
    config,
    service,
    handleActeSaved,
    handleIndividuSaved,
  ]);

  /**
   * Sidebar gauche : la liste des actes du type courant, avec recherche et
   * navigation vers leur propre fiche au clic — même montage que la sidebar
   * de `RecenseFiche` (recensement-citoyen), qui pousse la liste des
   * recensés via `sidebars.left`.
   */
  const sidebars = React.useMemo(
    () => ({ left: <config.ListeSidebar sidebar /> }),
    [config],
  );

  const mentionsPanel = React.useMemo(
    () => (
      <Fiche.Panel key="mentions" id="mentions" title="MENTION(S)" open>
        {!_.isNil(acte?.identifiant) && (
          <MentionListeContainer
            key={acte.identifiant}
            cleActe={config.cleMentionActe}
            identifiantActe={acte.identifiant}
            autoOpenAjoutMention={autoOpenAjoutMention}
          />
        )}
      </Fiche.Panel>
    ),
    [acte?.identifiant, config.cleMentionActe, autoOpenAjoutMention],
  );

  return (
    <Fiche
      key="ActeFiche"
      type="acte"
      header={config.getTitre(dossier)}
      loading={loading}
      sidebars={sidebars}
    >
      {/*
        Les panneaux sont assemblés en tableau et non listés : `Fiche` dérive ses
        ancres de ses enfants, un `null` inséré au milieu ne satisfait pas son
        contrat. Le panneau complémentaire n'existe que si le registre le décrit.
      */}
      {[
        informationsRegistrePanel,
        informationsEvenementPanel,
        ...(complementPanel ? [complementPanel] : []),
        informationsPersonnesPanel,
        mentionsPanel,
      ]}
    </Fiche>
  );
};

export default ActeFiche;
